import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  Chip
} from "@mui/material";
import { GlobalContext } from "../../GlobalProvider";
import { createEntity, getEntityById, updateEntityById, createVLMConfig, updateVLMConfig, getVLMConfig } from "../../api";
import Header from "../Header";
import { useParams, useNavigate } from "react-router-dom";
import LocationSelector from "../LocationSelector";
import DisplayIcon from "../Icon/DisplayIcon";
import MapIcon from "@mui/icons-material/Map";
import IconButton from "@mui/material/IconButton";
import FileStorageComponent from "../FileStorageComponent";
import VLMPromptConfigDialog from "../VLM/VLMPromptConfigDialog";

const CreateEditEntity = () => {
  const { fullApp } = useContext(GlobalContext);
  const { entityId } = useParams();
  const navigate = useNavigate();
  const [currentEntity, setCurrentEntity] = useState(null);
  const [entityData, setEntityData] = useState({ name: "" });
  const [sensorData, setSensorData] = useState({});
  const [actionData, setActionData] = useState({});
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [error, setError] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [entityImage, setEntityImage] = useState({url: "", fileName: ""});
  const [vlmConfigs, setVlmConfigs] = useState({});
  const [showVLMConfig, setShowVLMConfig] = useState(false);
  const [currentVLMSensor, setCurrentVLMSensor] = useState(null);

  useEffect(() => {
    if (entityId) {
      setIsEditMode(true);
      fetchEntityDetails(entityId);
    }
  }, [entityId]);

  const fetchEntityDetails = async (id) => {
    try {
      const response = await getEntityById(id);
      const entity = response.data;

      setCurrentEntity(entity.entityTypeId);
      setEntityData({
        name: entity.name,
        ...entity.keys.reduce((acc, key) => {
          acc[key.keyName] = key.value;
          return acc;
        }, {}),
      });
      setEntityImage(entity.image);
      setSensorData(
        entity.sensors.reduce((acc, sensor) => {
          acc[sensor.sensorId] = {
            externalId: sensor.externalId,
            latitude: sensor.latitude || "",
            longitude: sensor.longitude || "",
          };
          return acc;
        }, {})
      );

      setActionData(
        entity.actions.reduce((acc, action) => {
          acc[action.actionId] = action.requestParams.reduce((params, param) => {
            params[param.paramName] = param.value;
            return params;
          }, {});
          return acc;
        }, {})
      );

      // Load VLM configurations for VLM sensors
      await loadExistingVLMConfigs(entity);
    } catch (error) {
      console.error("Error fetching entity:", error);
      setError("Failed to fetch entity details.");
    }
  };
  const handleGeolocationUpdate = (lat, lng, addr) => {
    handleInputChange("latitude", lat);
    handleInputChange("longitude", lng);
    handleInputChange("address", addr);
    setIsMapOpen(false);

  };
  const validateForm = () => {
    if (!entityData.name.trim()) return false;

    // Check all keys are filled
    const areKeysValid = currentEntity.keys.every((key) => {
      return String(entityData[key])?.trim();
    });

    // Check all sensors are valid
    const areSensorsValid = currentEntity.sensors.filter((sensor) => 
      !sensor.derived || sensor.sensor_name === 'Dynamic VLM Sensor' || sensor.uiType === 'dynamic_vlm'
    ).every((sensor) => {
      const sensorDataEntry = sensorData[sensor._id] || {};
      
      // For VLM sensors, check if VLM config is provided
      if (sensor.sensor_name === 'Dynamic VLM Sensor' || sensor.sensor_name === 'Dynamic VLM' || sensor.uiType === 'dynamic_vlm') {
        return vlmConfigs[sensor._id] && sensorDataEntry.externalId?.trim();
      }
      
      if (!sensorDataEntry.externalId?.trim()) return false;
      if (sensor.sensor_name === "GPS" && currentEntity.geoType === "static") {
        return sensorDataEntry.latitude?.trim() && sensorDataEntry.longitude?.trim();
      }
      return true;
    });

    // Check all actions are valid
    const areActionsValid = currentEntity.actions.every((action) => {
      const actionParams = actionData[action._id] || {};
      return action.requestParams?.every((param) => actionParams[param]?.trim());
    });

    return areKeysValid && areSensorsValid && areActionsValid;
  };

  // Update validation whenever the form data changes
  useEffect(() => {
    if (currentEntity) {
      setIsFormValid(validateForm());
    }
  }, [entityData, sensorData, actionData, currentEntity, vlmConfigs]);

  if (!fullApp) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="textSecondary">
          No App Found. Please select a valid App.
        </Typography>
      </Box>
    );
  }

  const handleStartEntityCreation = (entity) => {
    setCurrentEntity(entity);
    const initialData = { name: "" };
    entity.keys.forEach((key) => (initialData[key] = ""));

    setEntityData(initialData);
    setSensorData({});
    setActionData({});
    setVlmConfigs({});
  };

  const handleInputChange = (key, value) => {
    setEntityData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSensorDataChange = (sensorId, key, value) => {
    setSensorData((prev) => ({
      ...prev,
      [sensorId]: {
        ...(prev[sensorId] || {}),
        [key]: value,
      },
    }));
  };

  const handleActionDataChange = (actionId, key, value) => {
    setActionData((prev) => ({
      ...prev,
      [actionId]: {
        ...(prev[actionId] || {}),
        [key]: value,
      },
    }));
  };

  const handleVLMConfigure = (sensor) => {
    setCurrentVLMSensor(sensor);
    setShowVLMConfig(true);
  };

  const handleVLMPromptSave = (promptConfigs) => {
    if (currentVLMSensor) {
      setVlmConfigs((prev) => ({
        ...prev,
        [currentVLMSensor._id]: promptConfigs,
      }));
      setShowVLMConfig(false);
      setCurrentVLMSensor(null);
    }
  };

  const handleVLMConfigCancel = () => {
    setShowVLMConfig(false);
    setCurrentVLMSensor(null);
  };

  const createVLMConfigurations = async (createdEntity, vlmConfigs) => {
    try {
      // Find VLM sensors in the created entity and create VLM configurations
      for (const sensor of createdEntity.sensors) {
        const vlmConfig = vlmConfigs[sensor.sensorId];
        if (vlmConfig) {
          console.log('Creating VLM configuration for sensor:', sensor._id);
          await createVLMConfig({
            entitySensorId: sensor._id,
            entityId: createdEntity._id,
            appId: fullApp._id,
            promptConfigs: Array.isArray(vlmConfig) ? vlmConfig : [vlmConfig]
          });
          console.log('VLM configuration created successfully');
        }
      }
    } catch (error) {
      console.error('Error creating VLM configurations:', error);
      // Don't throw error to avoid breaking the entity creation flow
      setSnackbar({ 
        open: true, 
        message: "Entity created but VLM configuration failed. You can reconfigure later.", 
        severity: "warning" 
      });
    }
  };

  const updateVLMConfigurations = async (entityId, vlmConfigs) => {
    try {
      // Get the current entity to find sensor IDs
      const entityResponse = await getEntityById(entityId);
      const entity = entityResponse.data;
      
      for (const sensor of entity.sensors) {
        const vlmConfig = vlmConfigs[sensor.sensorId];
        if (vlmConfig) {
          try {
            // Try to update existing VLM config
            console.log('Updating VLM configuration for sensor:', sensor._id);
            await updateVLMConfig(sensor._id, { 
              promptConfigs: Array.isArray(vlmConfig) ? vlmConfig : [vlmConfig] 
            });
            console.log('VLM configuration updated successfully');
          } catch (updateError) {
            // If config doesn't exist, create it
            if (updateError.response?.status === 404) {
              console.log('VLM config not found, creating new one for sensor:', sensor._id);
              await createVLMConfig({
                entitySensorId: sensor._id,
                entityId: entityId,
                appId: fullApp._id,
                promptConfigs: Array.isArray(vlmConfig) ? vlmConfig : [vlmConfig]
              });
              console.log('VLM configuration created successfully');
            } else {
              throw updateError;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error updating VLM configurations:', error);
      setSnackbar({ 
        open: true, 
        message: "Entity updated but VLM configuration failed. You can reconfigure later.", 
        severity: "warning" 
      });
    }
  };

  const loadExistingVLMConfigs = async (entity) => {
    try {
      const vlmConfigsToLoad = {};
      
      // Check each sensor to see if it's a VLM sensor and load its config
      for (const sensor of entity.sensors) {
        // Check if this sensor has VLM configuration from the entity data
        if (sensor.vlmConfig) {
          vlmConfigsToLoad[sensor.sensorId] = Array.isArray(sensor.vlmConfig) ? sensor.vlmConfig : [sensor.vlmConfig];
        } else {
          // Try to load from VLM backend
          try {
            const response = await getVLMConfig(sensor._id);
            if (response.data && response.data.promptConfigs) {
              vlmConfigsToLoad[sensor.sensorId] = response.data.promptConfigs;
            } else if (response.data && response.data.promptConfig) {
              // Backward compatibility
              vlmConfigsToLoad[sensor.sensorId] = [response.data.promptConfig];
            }
          } catch (error) {
            // VLM config doesn't exist, which is fine
            console.log('No VLM config found for sensor:', sensor._id);
          }
        }
      }
      
      setVlmConfigs(vlmConfigsToLoad);
    } catch (error) {
      console.error('Error loading existing VLM configurations:', error);
    }
  };

  const handleImageUpload = (imageData) => {
    setEntityImage(imageData);
    setEntityData(prev => ({
      ...prev,
      image: imageData
    }));
  };

  const handleSubmit = async () => {
    if (!entityData.name.trim()) {
      setSnackbar({ open: true, message: "Please provide a name for the entity.", severity: "warning" });
      return;
    }

    const entityObject = {
      appId: fullApp._id,
      entityTypeId: currentEntity._id,
      name: entityData.name,
      image: entityImage,
      isGeoLocated: currentEntity.isGeoLocated && currentEntity.geoType === "static" ? true : false,
      location: entityData.latitude ? { type: 'point', coordinates: [entityData.longitude, entityData.latitude] } : null,
      address: entityData.address,
      parentEntityId: null,
      keys: currentEntity.keys.map((key) => ({
        keyName: key,
        value: entityData[key],
      })),
      sensors: currentEntity.sensors.map((sensor) => ({
        sensorId: sensor._id,
        externalId: sensorData[sensor._id]?.externalId || "",
        ...(sensor.sensor_name === "GPS" && currentEntity.geoType === "static"
          ? {
            latitude: sensorData[sensor._id]?.latitude || "",
            longitude: sensorData[sensor._id]?.longitude || "",
          }
          : {}),
        // Add VLM configuration if it's a VLM sensor
        ...(vlmConfigs[sensor._id] ? { vlmConfig: vlmConfigs[sensor._id] } : {}),
      })),
      actions: currentEntity.actions.map((action) => ({
        actionId: action._id,
        requestParams: action.requestParams.map((param) => ({
          paramName: param,
          value: actionData[action._id]?.[param] || "",
        })),
      })),
    };

    try {
      let createdEntity;
      if (isEditMode) {
        await updateEntityById(entityId, entityObject);
        
        // Update VLM configurations for any VLM sensors in edit mode
        if (Object.keys(vlmConfigs).length > 0) {
          await updateVLMConfigurations(entityId, vlmConfigs);
        }
        
        setSnackbar({ open: true, message: `Entity "${entityData.name}" updated successfully!`, severity: "success" });
      } else {
        const response = await createEntity(entityObject);
        createdEntity = response.data;
        setSnackbar({ open: true, message: `Entity "${entityData.name}" created successfully!`, severity: "success" });
        
        // Create VLM configurations for any VLM sensors
        if (createdEntity && Object.keys(vlmConfigs).length > 0) {
          await createVLMConfigurations(createdEntity, vlmConfigs);
        }
      }
      setTimeout(() => navigate("/entities"), 1500);
    } catch (error) {
      console.error("Error saving entity:", error);
      setSnackbar({ open: true, message: "Failed to save entity. Please try again.", severity: "error" });
    }
  };

  return (
    <Box p={3}>
      <Header title={`Create Entities for ${fullApp.name}`} />
      {!currentEntity ? (
        <Grid container spacing={2}>
          {fullApp.entities.map((entity) => (
            <Grid item xs={12} sm={6} md={4} key={entity._id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <DisplayIcon name={entity.icon} />
                    <Box>
                      <Typography variant="h6">{entity.name}</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {entity.isGeoLocated ? `Geo-Type: ${entity.geoType}` : 'Not Geo-Located'}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleStartEntityCreation(entity)}
                    sx={{ mt: 2 }}
                  >
                    Create {entity.name}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box>
          <Typography variant="h5" gutterBottom>
            Create {currentEntity.name}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Entity Name"
                value={entityData.name || ""}
                onChange={(e) => handleInputChange("name", e.target.value)}
                fullWidth
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Entity Image
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FileStorageComponent
                  onUploadSuccess={handleImageUpload}
                  acceptedFileTypes={["image/*"]}
                  maxFileSize={5}
                  folderName="entities"
                  initialFileUrl={entityImage}
                />
                {entityImage?.url && (
                  <img 
                    src={entityImage.url} 
                    alt="Entity" 
                    style={{ 
                      maxWidth: '200px', 
                      maxHeight: '200px', 
                      objectFit: 'contain' 
                    }} 
                  />
                )}
              </Box>
            </Grid>
            {currentEntity.keys.map((key) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  label={key}
                  value={entityData[key] || ""}
                  onChange={(e) => handleInputChange(key, e.target.value)}
                  fullWidth
                  variant="outlined"
                />
              </Grid>
            ))}
            {currentEntity.isGeoLocated && currentEntity.geoType === "static" && (
              <Grid item xs={12}  sm={6}>
                <IconButton color="primary" onClick={() => setIsMapOpen(true)}>
                  <MapIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
          <Box mt={3}>
            <Typography variant="h6">Sensors</Typography>
            <List>
              {currentEntity.sensors.filter((sensor) => 
                !sensor.derived || sensor.sensor_name === 'Dynamic VLM Sensor' || sensor.sensor_name === 'Dynamic VLM' || sensor.uiType === 'dynamic_vlm'
              ).map((sensor) => {
                const isVLMSensor = sensor.sensor_name === 'Dynamic VLM Sensor' || sensor.sensor_name === 'Dynamic VLM' || sensor.uiType === 'dynamic_vlm';
                const vlmConfig = vlmConfigs[sensor._id];
                
                return (
                  <ListItem key={sensor._id} sx={{ flexDirection: "column", alignItems: "flex-start" }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ListItemText
                        primary={`Sensor: ${sensor.sensor_name}`}
                        secondary={sensor.description}
                      />
                      {isVLMSensor && (
                        <Chip label="VLM" size="small" color="primary" variant="outlined" />
                      )}
                    </Box>
                    
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="External ID"
                          placeholder="Enter External ID"
                          fullWidth
                          value={sensorData[sensor._id]?.externalId || ""}
                          onChange={(e) =>
                            handleSensorDataChange(sensor._id, "externalId", e.target.value)
                          }
                        />
                      </Grid>
                      
                      {isVLMSensor && (
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                              variant={vlmConfig ? "outlined" : "contained"}
                              color="primary"
                              onClick={() => handleVLMConfigure(sensor)}
                            >
                              {vlmConfig ? "Reconfigure VLM" : "Configure VLM"}
                            </Button>
                            {vlmConfig && Array.isArray(vlmConfig) && vlmConfig.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {vlmConfig.map((config, index) => (
                                  <Chip 
                                    key={index}
                                    label={config.title || `Prompt ${index + 1}`} 
                                    color="success" 
                                    variant="outlined"
                                    size="small"
                                  />
                                ))}
                                <Chip 
                                  label={`${vlmConfig.length} prompt${vlmConfig.length !== 1 ? 's' : ''}`}
                                  color="primary"
                                  size="small"
                                />
                              </Box>
                            )}
                          </Box>
                          {!vlmConfig && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              <Typography variant="caption">
                                VLM configuration required for this sensor
                              </Typography>
                            </Alert>
                          )}
                        </Grid>
                      )}
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                  </ListItem>
                );
              })}
            </List>
          </Box>
          <Box mt={3}>
            <Typography variant="h6">Actions</Typography>
            <List>
              {currentEntity.actions.map((action) => (
                <ListItem key={action._id}>
                  <img src={action.imageUrl} alt={action.name} width="50" style={{ marginRight: '10px' }} />
                  <ListItemText
                    primary={action.name}
                    secondary={action.description}
                  />
                  <Grid container spacing={2}>
                    {action.requestParams?.map((param) => (
                      <Grid item xs={12} sm={6} key={param}>
                        <TextField
                          label={param}
                          value={(actionData[action._id]?.[param]) || ""}
                          onChange={(e) =>
                            handleActionDataChange(action._id, param, e.target.value)
                          }
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                    ))}
                  </Grid>
                </ListItem>
              ))}
            </List>
          </Box>
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setCurrentEntity(null)}
            >
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!isFormValid}>
              Submit
            </Button>
          </Box>
        </Box>
      )}
      <Dialog open={isMapOpen} onClose={() => setIsMapOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Location</DialogTitle>
        <DialogContent>
          <LocationSelector
            initialLocation={{ lat: entityData.latitude , lng: entityData.longitude }}
            onLocationSelect={handleGeolocationUpdate}
          />
        </DialogContent>
      </Dialog>

      {/* Snackbar for success/error messages */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* VLM Prompt Configuration Dialog */}
      <VLMPromptConfigDialog
        open={showVLMConfig}
        onClose={handleVLMConfigCancel}
        onSave={handleVLMPromptSave}
        initialPrompts={vlmConfigs[currentVLMSensor?._id] || []}
        sensorName={currentVLMSensor?.sensor_name || 'Dynamic VLM Sensor'}
      />
    </Box>
  );
};

export default CreateEditEntity;
